<script setup>
import { computed, ref, onMounted, nextTick, getCurrentInstance } from 'vue';

const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  size: {
    type: String,
    default: 'md',
    validator: value => ['xs', 'sm', 'md'].includes(value),
  },
  message: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  options: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
]);

// Generate a unique ID per component instance when `id` prop is not provided.
const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);
const isSelect = computed(() => props.type === 'select');

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputOutlineClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'outline-n-ruby-8 dark:outline-n-ruby-8 hover:outline-n-ruby-9 dark:hover:outline-n-ruby-9 disabled:outline-n-ruby-8 dark:disabled:outline-n-ruby-8';
    default:
      return 'outline-n-slate-6 dark:outline-n-slate-6 hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-slate-6 dark:disabled:outline-n-slate-6 focus:outline-n-brand dark:focus:outline-n-brand';
  }
});

const handleInput = event => {
  let value = event.target.value;
  // Convert to number if type is number and value is not empty
  if (props.type === 'number' && value !== '') {
    value = Number(value);
  }
  emit('update:modelValue', value);
  emit('input', event);
};

// date/time pickers and selects often commit via `change` rather than (or in addition to) `input`
const handleChange = event => {
  if (!['date', 'time', 'datetime-local', 'select'].includes(props.type))
    return;
  handleInput(event);
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
};

const sizeClass = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h-8 !px-2 !py-1.5 text-xs';
    case 'sm':
      return 'h-8 !px-3 !py-2 text-sm';
    case 'md':
      return 'h-10 !px-3 !py-2.5 text-sm';
    default:
      return 'h-10 !px-3 !py-2.5 text-sm';
  }
});

// Extra right padding so the native chevron isn't flush against the edge.
const selectSizeClass = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'h-8 !pl-2 !pr-7 !py-1.5 text-xs';
    case 'sm':
      return 'h-8 !pl-3 !pr-8 !py-2 text-sm';
    case 'md':
      return 'h-10 !pl-3 !pr-9 !py-2.5 text-sm';
    default:
      return 'h-10 !pl-3 !pr-9 !py-2.5 text-sm';
  }
});

const baseControlClass =
  'block w-full reset-base !mb-0 outline outline-1 border-none border-0 outline-offset-[-1px] rounded bg-white dark:bg-n-solid-3 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 font-medium antialiased text-n-slate-12 transition-all duration-500 ease-in-out';

const inputOnlyClass =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

const handleBlur = event => {
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <label
      v-if="label"
      :for="uniqueId"
      class="mb-0.5 text-sm font-medium text-n-slate-12"
    >
      {{ label }}
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
      <span v-if="required" class="text-n-ruby-9">*</span>
    </label>
    <!-- Added prefix slot to allow adding icons to the input -->
    <slot name="prefix" />
    <select
      v-if="isSelect"
      :id="uniqueId"
      v-bind="$attrs"
      ref="inputRef"
      :value="modelValue"
      class="cursor-pointer"
      :class="[
        customInputClass,
        inputOutlineClass,
        selectSizeClass,
        baseControlClass,
        {
          error: messageType === 'error',
          focus: isFocused,
        },
      ]"
      :disabled="disabled"
      :required="required"
      @change="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
    >
      <option
        v-for="option in options"
        :key="String(option.value)"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
    <input
      v-else
      :id="uniqueId"
      v-bind="$attrs"
      ref="inputRef"
      :value="modelValue"
      :class="[
        customInputClass,
        inputOutlineClass,
        sizeClass,
        baseControlClass,
        inputOnlyClass,
        {
          error: messageType === 'error',
          focus: isFocused,
        },
      ]"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :min="
        ['date', 'datetime-local', 'time'].includes(type) && min
          ? min
          : undefined
      "
      :max="
        ['date', 'datetime-local', 'time', 'number'].includes(type) && max
          ? max
          : undefined
      "
      @input="handleInput"
      @change="handleChange"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
    />
    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
